// import React, { useCallback, useEffect } from 'react';
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';

import {
  updateItem,
  deleteItem,
  deleteItemEverywhere,
  uploadImage,
  deleteImage,
  resetImages,
  revertItem,
  setCurrentItemId,
  setImageSidebarFocus,
  setItemChanges,
  takeItemSnapshot,
  deleteItemSnapshot,
  setSnapshotStatus,
} from '../../../store/templateItems';
import { showSnapshotToast } from '../../../store/toasts';
// import useSnapshotPolling from '../../../hooks/useSnapshotPolling';

import { getOptionsFromStrings } from '../../../utils/options';
import { toggleWord } from '../../../utils/words';
import { KEYS } from '../../../utils/keycode';
import { SNAPSHOT_STATUS } from '../../../utils/snapshotStatus';
import { itemNotEmpty } from '../../../utils/validate';
import { roundWithDecimal, addCommasToThousands } from '../../../utils/format';
import { withDebounce } from '../../../utils/debounce';

import useAuth from '../../../hooks/useAuth';

import DropdownInput from '../../DropdownInput';
import CompleteButtonWithModal from '../../CopleteButtonWithModal';
import ImageUploader from '../../ImageUploader';
import MatchingItemsCounter from '../../MatchingItemsCounter';
import PriceInput from '../../PriceInput';
import ViewMatchesButton from '../../ViewMatchesButton';
import TemplateItemNameInput from '../../TemplateItemNameInput';
import RevertButton from '../../RevertButton';
import ColorizedInput from '../../ColorizedInput';
import InputWithLock from '../../InputWithLock';
import ViewQueryButton from '../../ViewQueryButton';
import DeleteButtonWithDropdown from '../../DeleteButtonWithDropdown';
import DeleteButtonWithModal from '../../DeleteButtonWithModal';
import SnapshotButton from '../../SnapshotButton';

import styles from '../styles.module.css';

const ExistingTemplateItemRow = ({
  item,
  onItemCompleted,
  withoutDelete,
  idx,
  last,
  rawIndex,
}) => {
  const dispatch = useDispatch();
  const { isAdmin } = useAuth();

  const { brands, colors, categories } = useSelector((store) => store.dictionaries);
  const columns = useSelector((store) => store.columns.items);
  const {
    percent, percent2, percent3, percent4, currency,
  } = useSelector((store) => store.editableTemplate);
  const currentItemId = useSelector((store) => store.templateItems.currentItemId);
  const currentItemChanges = useSelector((store) => {
    if (currentItemId === item.id) {
      return store.templateItems.currentItemChanges;
    }
    return null;
  });

  const getFieldValue = (
    fieldName,
    defaultValue = '',
  ) => currentItemChanges?.[fieldName] ?? (item[fieldName] || defaultValue);

  // const { startPolling } = useSnapshotPolling(item.id);

  const itemHasSnapshot = getFieldValue('snapshot_taken_at', null) !== null;
  const itemHasChanges = currentItemChanges && Object.keys(currentItemChanges)
    .filter((it) => it !== 'count').length > 0;
  const locked = itemHasSnapshot || getFieldValue('completed', false);
  const itemHasImages = getFieldValue('template_images', []).length > 0 || getFieldValue('product_images', []).length > 0;
  const doubleLocked = itemHasImages && !getFieldValue('completed', false);
  // Typers may edit only their own items. The backend sets `is_editable: false` on other
  // users' items; admins and own/new rows stay editable. `rowLocked` makes non-editable
  // rows read-only (disabledRow = pointer-events:none) without touching admin behavior.
  const canEdit = isAdmin || item.is_editable !== false;
  const rowLocked = locked || !canEdit;
  // A typer may not edit the descriptive fields of a query they copied from the blue box
  // (backend sets is_query_locked when the row's duplicate_id is set). They can still select,
  // snapshot and complete it, so this locks only the query-field cells, not the whole row.
  const queryFieldsLocked = !isAdmin && item.is_query_locked === true;

  const itemId = item.id;

  const onArbitraryInputChange = useCallback(async (value, field) => {
    await dispatch(updateItem({ id: itemId, field, value }));
  }, [itemId, dispatch]);

  // Only the request to the server is debounced. The typed value always reaches the store
  // right away (see onArbitraryWithDebounceChange): the inputs force-sync their value from
  // the store, so a value that lands there late gets pushed back into the input and eats
  // whatever was typed in the meantime.
  // Memoized on the item id so a re-render can never leave a second timer armed and flush
  // an older value after a newer one.
  const debouncedSave = useMemo(() => withDebounce((value, field) => {
    dispatch(updateItem({ id: itemId, field, value }));
  }), [itemId, dispatch]);

  // Flush rather than cancel, so a save pending when the row unmounts is not dropped.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  const onArbitraryWithDebounceChange = useCallback((value, field) => {
    dispatch(setItemChanges({ id: itemId, field, value }));
    debouncedSave(value, field);
  }, [itemId, dispatch, debouncedSave]);

  const onTextInputChange = (e) => {
    const { value } = e.target;
    const field = e.target.getAttribute('field');
    onArbitraryWithDebounceChange(value, field);
  };
  const onDateInputChange = (e) => {
    const value = e.target.value || null;
    const field = e.target.getAttribute('field');
    onArbitraryWithDebounceChange(value, field);
  };
  const onMultiselectChange = useCallback((options, field) => {
    onArbitraryWithDebounceChange(options.map((o) => o.value), field);
  }, [onArbitraryWithDebounceChange]);

  const onComplete = async (value, field) => {
    await dispatch(setItemChanges({ id: item.id, field, value }));
    await onArbitraryInputChange(value, field);

    if (value) {
      onItemCompleted();
    }

    return true;
  };

  const onKeyPress = async (e) => {
    if (e.code === KEYS.ENTER) {
      e.target.blur();
      await onComplete();
    } else if (e.code === KEYS.TAB) {
      e.preventDefault();
      e.target.blur();
      dispatch(setImageSidebarFocus(true));
    }
  };

  const onAddWordToName = async (word) => {
    const name = currentItemChanges?.name ?? (item.name || '');
    const newName = toggleWord(name, word);
    await dispatch(updateItem({
      id: item.id,
      value: newName,
      field: 'name',
    }));
    await dispatch(setItemChanges({
      id: item.id,
      value: newName,
      field: 'name',
    }));
  };

  const onFocus = () => {
    dispatch(setCurrentItemId({ id: item.id }));
  };

  const onTakeSnapshot = async () => {
    try {
      await dispatch(setSnapshotStatus({ id: item.id, status: SNAPSHOT_STATUS.SNAPSHOTTING }));
      await dispatch(takeItemSnapshot({ id: item.id }));
      dispatch(showSnapshotToast());
      // startPolling();
    } catch (error) {
      console.error('Failed to take snapshot:', error);
      // Revert status on error
      await dispatch(setSnapshotStatus({ id: item.id, status: SNAPSHOT_STATUS.NO_SNAPSHOT }));
    }
  };

  const onDeleteSnapshot = async () => {
    try {
      await dispatch(deleteItemSnapshot({ id: item.id }));
    } catch (error) {
      console.error('Failed to delete snapshot:', error);
    }
  };

  const snapshotStatus = getFieldValue('snapshot_status', SNAPSHOT_STATUS.NO_SNAPSHOT);
  // const isSnapshotting = snapshotStatus === SNAPSHOT_STATUS.SNAPSHOTTING;

  // useEffect(() => {
  //   if (isSnapshotting) {
  //     startPolling();
  //   }
  // }, []);
  const shouldShowSnapshotButton = itemHasImages
    && item.last_viewed_by === null
    && getFieldValue('ai_threshold', 0) > 0;

  const itemsCount = getFieldValue(snapshotStatus === SNAPSHOT_STATUS.TAKEN ? 'snapshot_count' : 'count', 0);

  return (
    <tr onClick={onFocus} className={cx(currentItemId === item.id && styles.selectedRow)}>
      <td className={cx(!columns.number && styles.disabledColumn, rowLocked && styles.disabledRow)}>
        <p>{idx + 1}</p>
      </td>
      <td className={cx(
        !columns.sku && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <Form.Control
            size="sm"
            field="sku"
            id={`sku-${item.id}`}
            value={getFieldValue('sku')}
            required
            onChange={onTextInputChange}
            onKeyPress={onKeyPress}
            onFocus={onFocus}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.manual_price && styles.disabledColumn,
        (!canEdit || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={locked}>
          <PriceInput
            field="manual_price"
            updatedAt={item.price_updated_at}
            onChange={onTextInputChange}
            onKeyPress={onKeyPress}
            value={getFieldValue('manual_price')}
            onFocus={onFocus}
            id={`price-${item.id}`}
          />
        </InputWithLock>
        {[percent, percent2, percent3, percent4].map((p) => p > 0 && (
          <div className="text-secondary">
            <small>
              {addCommasToThousands(roundWithDecimal(getFieldValue('manual_price') * (p / 100) * currency))}
            </small>
          </div>
        ))}
      </td>
      <td className={cx(!columns.image && styles.disabledColumn)}>
        <ImageUploader
          onUpload={(image) => dispatch(uploadImage({ id: item.id, image }))}
          onDelete={(imageId) => dispatch(deleteImage({ id: item.id, imageId }))}
          onReset={() => dispatch(resetImages(item.id))}
          templateImages={item.template_images}
          productImages={item.product_images}
          disabled={rowLocked || queryFieldsLocked}
        />
      </td>
      <td
        className={cx(
          !columns.ai_threshold && styles.disabledColumn,
          (rowLocked || queryFieldsLocked) && styles.disabledRow,
        )}
        style={{ minWidth: 70 }}
      >
        <InputWithLock enabled={doubleLocked}>
          <Form.Control
            type="number"
            size="sm"
            field="ai_threshold"
            value={getFieldValue('ai_threshold', '')}
            step="0.1"
            min="0"
            max="100"
            onChange={onTextInputChange}
            onKeyPress={onKeyPress}
            onFocus={onFocus}
            id={`ai_threshold-${item.id}`}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.brands && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <div>
          <InputWithLock enabled={doubleLocked}>
            <DropdownInput
              id={`brands-${item.id}`}
              options={brands}
              initialValue={getOptionsFromStrings(getFieldValue('brands', []))}
              onChange={onMultiselectChange}
              name="brands"
              field="brands"
              onFocus={onFocus}
            />
          </InputWithLock>
          {Number.isInteger(itemsCount) && (
            <MatchingItemsCounter counter={itemsCount} className={styles.counter} />
          )}
        </div>
      </td>
      <td
        className={cx(
          !columns.name && styles.disabledColumn,
          (rowLocked || queryFieldsLocked) && styles.disabledRow,
        )}
        style={{ minWidth: 110 }}
      >
        <InputWithLock enabled={doubleLocked}>
          <TemplateItemNameInput
            onWordClick={onAddWordToName}
            itemId={item.id}
            id={`name-${item.id}`}
            input={getFieldValue('name')}
            options={item.nameOptions || []}
            brands={
              getOptionsFromStrings(getFieldValue('brands', [])).map((it) => it.value).join(';')
            }
            onChange={onArbitraryWithDebounceChange}
            onKeyPress={onKeyPress}
            field="name"
            completed={getFieldValue('completed', false)}
            isNotEmpty={itemNotEmpty(item, currentItemChanges)}
            short={rawIndex === 0 || rawIndex === 1}
            onFocus={onFocus}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.colors && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <DropdownInput
            id={`colors-${item.id}`}
            options={colors}
            initialValue={getOptionsFromStrings(getFieldValue('colors', []))}
            onChange={onMultiselectChange}
            name="colors"
            field="colors"
            onFocus={onFocus}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.description && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <ColorizedInput
            size="sm"
            value={getFieldValue('description')}
            required
            field="description"
            onChange={onTextInputChange}
            onKeyPress={onKeyPress}
            onFocus={onFocus}
            id={`description-${item.id}`}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.categories && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <DropdownInput
            id={`categories-${item.id}`}
            options={categories}
            initialValue={getOptionsFromStrings(
              getFieldValue('categories', []),
              categories,
            )}
            onChange={onMultiselectChange}
            name="categories"
            field="categories"
            onFocus={onFocus}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.fabric && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <ColorizedInput
            size="sm"
            field="fabric"
            value={getFieldValue('fabric')}
            required
            onChange={onTextInputChange}
            onKeyPress={onKeyPress}
            onFocus={onFocus}
            id={`fabric-${item.id}`}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.posted_at && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
        styles.postedAtColumn,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <Form.Control
            type="date"
            size="sm"
            field="posted_at"
            value={getFieldValue('posted_at')}
            onChange={onDateInputChange}
            onKeyPress={onKeyPress}
            onFocus={onFocus}
            id={`posted_at-${item.id}`}
          />
        </InputWithLock>
      </td>
      <td className={cx(
        !columns.note && styles.disabledColumn,
        (rowLocked || queryFieldsLocked) && styles.disabledRow,
      )}
      >
        <InputWithLock enabled={doubleLocked}>
          <Form.Control
            size="sm"
            field="note"
            value={getFieldValue('note')}
            required
            onChange={onTextInputChange}
            onKeyDown={onKeyPress}
            onFocus={onFocus}
            id={`note-${item.id}`}
            className={styles.noteInput}
          />
        </InputWithLock>
      </td>
      <td>
        {!withoutDelete && canEdit && (
          isAdmin ? (
            <DeleteButtonWithDropdown
              onDelete={() => dispatch(deleteItem(item.id))}
              onDeleteEverywhere={() => dispatch(deleteItemEverywhere(item))}
            />
          ) : (
            <DeleteButtonWithModal
              onDelete={() => dispatch(deleteItem(item.id))}
              modalTitle="Delete search query"
              modalText="Are you sure you want to delete this search query?"
              buttonClassName={styles.deleteButton}
            />
          )
        )}
      </td>
      {/* Hide complete button for matched queries (snapshot + completed) */}
      {canEdit && itemHasImages && !(itemHasSnapshot && getFieldValue('completed', false)) && (
        <td>
          <CompleteButtonWithModal
            value={getFieldValue('completed')}
            onChange={async (field, value) => {
              await onFocus();
              await onComplete(field, value);
            }}
            field="completed"
          />
        </td>
      )}
      {canEdit && shouldShowSnapshotButton && (
        <td>
          <SnapshotButton
            snapshotStatus={snapshotStatus}
            onTakeSnapshot={onTakeSnapshot}
            onDeleteSnapshot={onDeleteSnapshot}
          />
        </td>
      )}
      {isAdmin && item.view_matches && (
        <td>
          <ViewMatchesButton id={item.id} />
        </td>
      )}
      {isAdmin && !last && (
        <td>
          <ViewQueryButton id={item.id} />
        </td>
      )}
      {canEdit && !last && !locked && (
        <td>
          <RevertButton
            onClick={() => dispatch(revertItem({ id: item.id }))}
            disabled={!itemHasChanges}
          />
        </td>
      )}
    </tr>
  );
};

ExistingTemplateItemRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    template_id: PropTypes.number.isRequired,
    sku: PropTypes.string,
    brands: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string,
    // Present only for typers; the backend omits it for admins. `false` => read-only row.
    is_editable: PropTypes.bool,
    // Present only for typers; `true` => a blue-box-copied query whose descriptive fields are
    // locked (select/snapshot/complete still allowed). The backend omits it for admins.
    is_query_locked: PropTypes.bool,
    description: PropTypes.string,
    colors: PropTypes.arrayOf(PropTypes.string).isRequired,
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    fabric: PropTypes.string,
    note: PropTypes.string,
    completed: PropTypes.bool.isRequired,
    product_images: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    })).isRequired,
    template_images: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    })).isRequired,
    count: PropTypes.number,
    nameOptions: PropTypes.arrayOf(PropTypes.string),
    snapshot_taken_at: PropTypes.string,
    duplicate_id: PropTypes.number,
    manual_price: PropTypes.number,
    view_matches: PropTypes.bool,
    price_updated_at: PropTypes.string,
    ai_threshold: PropTypes.number,
    last_viewed_by: PropTypes.number,
  }).isRequired,
  idx: PropTypes.number,
  onItemCompleted: PropTypes.func.isRequired,
  withoutDelete: PropTypes.bool,
  last: PropTypes.bool.isRequired,
};

ExistingTemplateItemRow.defaultProps = {
  withoutDelete: false,
  idx: 0,
};

export default ExistingTemplateItemRow;
